import { FC, useMemo, useState } from 'react';
import { ApolloClient, useApolloClient } from '@apollo/client';
import {
    GEO_LOCATIONS_QUERY,
    ADDRESS_POSTCODE_SUGGESTION_QUERY
} from '@zoopla/listing-search-queries';
import { Combobox } from '@zoopla-eng/zds/combobox';
import {
    transformGeoLocationData,
    transformAddressPostcodeSuggestion
} from './transforms';

export type Region = 'ENGLAND' | 'SCOTLAND' | 'WALES';
type Props = {
    onInputTextChange?: (value?: string) => void;
    showLabel?: string;
    locationTextValue?: string;
    region?: Region;
    searchType?: 'property' | 'house-prices';
    onLocationSelect: (location: { id: string; text: string } | null) => void;
};

export type LocationType = {
    id: string;
    value: string | number | string[];
};

type Option = {
    id: string;
    text: string;
};

enum forSaleToRentPlaceholder {
    ENGLAND = 'eg. Oxford or NW3',
    SCOTLAND = 'e.g. Inverness or EH1',
    WALES = 'e.g. Maesteg or CF10'
}

const housePricesPlaceholder = 'e.g. Abbey Road, NW8 or Waterloo Station';

const getGeoLocationData =
    (client: ApolloClient<object>) => async (value: string | null) => {
        if (!value) return [];
        const { data } = await client.query({
            query: GEO_LOCATIONS_QUERY,
            variables: { locationPrefix: value }
        });

        return transformGeoLocationData(data.geoSuggestion);
    };

const getAddressPostcodeSuggestion =
    (locationTextValue: string, client: ApolloClient<object>) =>
    async (value: string | null) => {
        if (!value) return [];
        const { data } = await client.query({
            query: ADDRESS_POSTCODE_SUGGESTION_QUERY,
            variables: { addressPartial: value.toLowerCase() }
        });

        const locationTextValueSplit = locationTextValue.split(' ');
        const firstElement = locationTextValueSplit[0];
        const isFirstElementANumber = !isNaN(parseInt(firstElement, 10));

        const prefixElement = isFirstElementANumber ? firstElement : '';

        return transformAddressPostcodeSuggestion(
            data.getSuggestedAddresses,
            prefixElement
        );
    };

export const SimpleLocationSearchInput: FC<React.PropsWithChildren<Props>> = ({
    showLabel = 'Search location',
    searchType = 'property',
    locationTextValue = '',
    region = 'ENGLAND',
    onLocationSelect,
    onInputTextChange
}) => {
    const client = useApolloClient();
    const [selectedItem, setSelectedItem] = useState<Option | null>(null);

    const onSelectedItemChange = (selectedItem: Option | null) => {
        setSelectedItem(selectedItem);
        onLocationSelect(selectedItem);
    };

    const isHousePricesSearch = searchType === 'house-prices';

    const placeholderText = isHousePricesSearch
        ? housePricesPlaceholder
        : forSaleToRentPlaceholder[region];

    const getSuggestionFunction = useMemo(
        () =>
            isHousePricesSearch
                ? getAddressPostcodeSuggestion(locationTextValue, client)
                : getGeoLocationData(client),
        [isHousePricesSearch, client, locationTextValue]
    );

    return (
        <Combobox
            label={showLabel}
            initialItems={[]}
            selectedItem={selectedItem}
            fetchItems={getSuggestionFunction}
            onSelectedItemChange={onSelectedItemChange}
            placeholder={placeholderText}
            onInputValueChange={onInputTextChange}
        />
    );
};
